import React, { useState, useRef, useEffect } from 'react';
import { PaperAirplaneIcon, PaperClipIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { useChatStore } from '../hooks/useChatStore';
import { usersAPI, attachmentsAPI } from '../services/api';
import CompactEngineStatus from './CompactEngineStatus';
import QueryProgressBar from './QueryProgressBar';
import FileAttachment from './FileAttachment';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ALLOWED_TYPES = new Set([
  'image/png', 'image/jpeg', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain', 'text/csv', 'text/markdown',
  'text/x-python', 'text/javascript', 'text/html', 'text/css',
  'application/json', 'application/xml',
]);

interface CostStatus {
  current_cost: number;
  monthly_limit: number | null;
  can_proceed: boolean;
  percent_used: number | null;
}

interface ChatInputProps {
  onSubmit: (query: string, attachmentIds?: number[]) => void;
  disabled?: boolean;
}

interface PendingFile {
  file: File;
  previewUrl?: string;
}

export default function ChatInput({ onSubmit, disabled }: ChatInputProps) {
  const [query, setQuery] = useState('');
  const [costStatus, setCostStatus] = useState<CostStatus | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    queryMode,
    setQueryMode,
    isQuerying,
    engineStatuses,
    currentRound,
    totalRounds,
    elapsedTime,
    runningCost,
    isJudgePhase,
    roundStartTime,
  } = useChatStore();

  // Fetch cost status on mount and after queries complete
  useEffect(() => {
    const fetchCostStatus = async () => {
      try {
        const response = await usersAPI.getMyCostStatus();
        setCostStatus(response.data);
      } catch (err) {
        // Silently fail - user may not have limits set
        setCostStatus(null);
      }
    };

    fetchCostStatus();
  }, [isQuerying]); // Refetch when query completes

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200
      )}px`;
    }
  }, [query]);

  // Handle clipboard paste for images
  useEffect(() => {
    const handlePaste = async (e: ClipboardEvent) => {
      if (!textareaRef.current?.contains(e.target as Node)) return;

      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter(item => item.type.startsWith('image/'));

      if (imageItems.length > 0) {
        e.preventDefault();

        for (const item of imageItems) {
          const file = item.getAsFile();
          if (file) {
            // Create a meaningful filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, '');
            const extension = file.type.split('/')[1] || 'png';
            const filename = `screenshot_${timestamp}.${extension}`;

            // Create a new File with proper name
            const namedFile = new File([file], filename, { type: file.type });

            // Add to pending files
            const previewUrl = URL.createObjectURL(namedFile);
            setPendingFiles(prev => [...prev, { file: namedFile, previewUrl }]);
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []);

  // Clean up preview URLs on unmount
  useEffect(() => {
    return () => {
      pendingFiles.forEach((pf) => {
        if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
      });
    };
  }, []);

  const isLimitExceeded = costStatus?.can_proceed === false;
  const isApproachingLimit = costStatus !== null && costStatus.percent_used !== null && costStatus.percent_used >= 80 && costStatus.can_proceed;

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    setUploadError(null);
    const newPending: PendingFile[] = [];

    for (const file of Array.from(files)) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`"${file.name}" exceeds 10 MB limit`);
        continue;
      }
      if (!ALLOWED_TYPES.has(file.type) && file.type !== '') {
        // Check extension fallback for files with empty mime type
        const ext = file.name.split('.').pop()?.toLowerCase();
        const extAllowed = ['py', 'js', 'ts', 'jsx', 'tsx', 'html', 'css', 'json', 'xml', 'md', 'csv', 'txt', 'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];
        if (!ext || !extAllowed.includes(ext)) {
          setUploadError(`"${file.name}" is not a supported file type`);
          continue;
        }
      }

      const previewUrl = file.type.startsWith('image/')
        ? URL.createObjectURL(file)
        : undefined;

      newPending.push({ file, previewUrl });
    }

    setPendingFiles((prev) => [...prev, ...newPending]);
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || disabled || isQuerying || isLimitExceeded) return;

    let attachmentIds: number[] | undefined;

    if (pendingFiles.length > 0) {
      setIsUploading(true);
      setUploadError(null);
      try {
        const response = await attachmentsAPI.upload(
          pendingFiles.map((pf) => pf.file)
        );
        attachmentIds = response.data.attachments.map((a: { id: number }) => a.id);
      } catch (err: any) {
        setUploadError(
          err.response?.data?.detail || 'Failed to upload files'
        );
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    // Clean up preview URLs
    pendingFiles.forEach((pf) => {
      if (pf.previewUrl) URL.revokeObjectURL(pf.previewUrl);
    });

    onSubmit(query.trim(), attachmentIds);
    setQuery('');
    setPendingFiles([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const submitKey = localStorage.getItem('quorum_submit_key') || 'enter';

    if (e.key === 'Enter') {
      if (submitKey === 'ctrl-enter') {
        // Ctrl+Enter (or Cmd+Enter on Mac) to submit
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          handleSubmit(e);
        }
        // Plain Enter just adds a new line (default behavior)
      } else {
        // Enter to submit, Shift+Enter for new line
        if (!e.shiftKey) {
          e.preventDefault();
          handleSubmit(e);
        }
      }
    }
  };

  return (
    <div className="border-t border-surface-700 bg-surface-800 p-4">
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
        {/* Compact progress indicator when querying */}
        {isQuerying && engineStatuses.length > 0 && (
          <div className="mb-3 space-y-2">
            <CompactEngineStatus
              statuses={engineStatuses}
              currentRound={currentRound}
              totalRounds={totalRounds}
              totalCost={runningCost}
              elapsedTime={elapsedTime}
              isJudgePhase={isJudgePhase}
            />
            <QueryProgressBar
              currentRound={currentRound}
              totalRounds={totalRounds}
              isJudgePhase={isJudgePhase}
              roundStartTime={roundStartTime}
            />
          </div>
        )}

        {/* Cost limit warning */}
        {isLimitExceeded && (
          <div className="mb-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
            <NoSymbolIcon className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-red-400 font-medium">Monthly cost limit reached</p>
              <p className="text-xs text-red-400/80">
                You've used ${costStatus?.current_cost.toFixed(2)} of your ${costStatus?.monthly_limit?.toFixed(2)} limit.
                Contact your admin to increase your limit.
              </p>
            </div>
          </div>
        )}

        {isApproachingLimit && (
          <div className="mb-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-yellow-400 font-medium">Approaching cost limit</p>
              <p className="text-xs text-yellow-400/80">
                You've used ${costStatus?.current_cost.toFixed(2)} of your ${costStatus?.monthly_limit?.toFixed(2)} monthly limit ({costStatus?.percent_used?.toFixed(0)}%).
              </p>
            </div>
          </div>
        )}

        {/* Upload error */}
        {uploadError && (
          <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-xs text-red-400">{uploadError}</p>
          </div>
        )}

        {/* Mode toggle */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-surface-400">Mode:</span>
          <button
            type="button"
            onClick={() => setQueryMode('quick')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              queryMode === 'quick'
                ? 'bg-primary-600 text-white'
                : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
            }`}
          >
            Quick
          </button>
          <button
            type="button"
            onClick={() => setQueryMode('deliberation')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors ${
              queryMode === 'deliberation'
                ? 'bg-primary-600 text-white'
                : 'bg-surface-700 text-surface-300 hover:bg-surface-600'
            }`}
          >
            Deliberation
          </button>
          <span className="text-xs text-surface-500 ml-2">
            {queryMode === 'quick'
              ? 'Single round query'
              : 'Two rounds with refinement'}
          </span>
        </div>

        {/* Attached files chips */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {pendingFiles.map((pf, i) => (
              <FileAttachment
                key={`${pf.file.name}-${i}`}
                filename={pf.file.name}
                fileSize={pf.file.size}
                mimeType={pf.file.type}
                previewUrl={pf.previewUrl}
                onRemove={() => removePendingFile(i)}
              />
            ))}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/png,image/jpeg,image/gif,image/webp,application/pdf,text/plain,text/csv,text/markdown,.py,.js,.ts,.jsx,.tsx,.html,.css,.json,.xml,.md"
          onChange={handleFilesSelected}
        />

        {/* Input area */}
        <div className="relative flex items-end gap-2 bg-surface-700 rounded-xl p-2">
          <button
            type="button"
            className="p-2 text-surface-400 hover:text-surface-200 transition-colors"
            title="Attach file"
            onClick={() => fileInputRef.current?.click()}
            disabled={isQuerying || isUploading}
          >
            <PaperClipIcon className="w-5 h-5" />
          </button>

          <textarea
            ref={textareaRef}
            id="chat-input"
            name="query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask something..."
            disabled={disabled || isQuerying || isUploading}
            className="flex-1 bg-transparent border-0 resize-none focus:ring-0 focus:outline-none text-surface-100 placeholder-surface-500 max-h-[200px]"
            rows={1}
          />

          <button
            type="submit"
            disabled={!query.trim() || disabled || isQuerying || isLimitExceeded || isUploading}
            className={`p-2 rounded-lg transition-colors ${
              query.trim() && !disabled && !isQuerying && !isLimitExceeded && !isUploading
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-surface-600 text-surface-400 cursor-not-allowed'
            }`}
          >
            {isQuerying || isUploading ? (
              <div className="w-5 h-5 border-2 border-surface-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <PaperAirplaneIcon className="w-5 h-5" />
            )}
          </button>
        </div>

        <p className="text-xs text-surface-500 mt-2 text-center">
          Quorum synthesizes responses from multiple AI models
        </p>
      </form>
    </div>
  );
}
